import React from 'react'
import { Card, CardBody, Heading, Skeleton, Flex } from 'uikit'
import styled from 'styled-components'
import { getBalanceNumber } from 'utils/formatBalance'
import { usePriceDmdUsdc } from 'state/hooks'
import { useTotalSupply, useBurnedBalance } from 'hooks/useTokenBalance'
import { useTranslation } from 'contexts/Localization'
import { getDmdAddress } from 'utils/addressHelpers'
import { LOCKED_FOR_NFT, LOCKED_OF_TEAM } from 'config'
import { BIG_TEN } from 'utils/bigNumber'
import Balance from 'components/Balance'

const StyledDmdStats = styled(Card)`
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  background: linear-gradient(102.27deg, #002130 2.6%, #7000d0 100.83%);
`

const Tag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  width: 63px;
  height: 21px;

  background: #9112ff;
  border-radius: 4px;

  font-style: normal;
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.19em;
`

const StyledHeading = styled(Heading)`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
`

const StyledBalance = styled(Balance)`
  font-weight: bold;
  font-size: 25px;
  margin-bottom: 8px;
  color: #fefacd;
`

const Row = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`

const StyledFlex = styled(Flex)`
  flex-direction: column;
  align-items: center;
`

const ValueLabel = styled.div`
  font-family: Poppins;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
  font-size: 10px;

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 12px;
  }
`

const StyledCardValue = styled.div`
  font-weight: bold;
  font-size: 12px;

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 14px;
  }
`

const DmdStats = () => {
  const { t } = useTranslation()
  const dmdPrice = usePriceDmdUsdc()
  const totalSupply = useTotalSupply()
  const burnedBalance = getBalanceNumber(useBurnedBalance(getDmdAddress()))
  const circulatingSupply = totalSupply
    ? getBalanceNumber(totalSupply) - burnedBalance - LOCKED_FOR_NFT - LOCKED_OF_TEAM
    : 0
  const marketCap = totalSupply ? totalSupply.multipliedBy(dmdPrice).div(BIG_TEN.pow(18)).toNumber() : null

  return (
    <StyledDmdStats isSecondary>
      <CardBody>
        <Tag>{t('Stats').toUpperCase()}</Tag>
        <StyledHeading>{t('Market Cap')}</StyledHeading>
        {marketCap && !Number.isNaN(marketCap) ? (
          <StyledBalance decimals={2} value={marketCap} prefix="$" />
        ) : (
          <Skeleton height={26} width={120} />
        )}
        <Row>
          <StyledFlex>
            <ValueLabel>{t('Total Supply')}</ValueLabel>
            {totalSupply ? (
              <StyledCardValue>
                <Balance decimals={0} value={getBalanceNumber(totalSupply)} />
              </StyledCardValue>
            ) : (
              <Skeleton height={20} width={40} />
            )}
          </StyledFlex>
          <StyledFlex>
            <ValueLabel>{t('Total Burned')}</ValueLabel>
            {burnedBalance ? (
              <StyledCardValue>
                <Balance decimals={0} value={burnedBalance} />
              </StyledCardValue>
            ) : (
              <Skeleton height={20} width={40} />
            )}
          </StyledFlex>
          <StyledFlex>
            <ValueLabel>{t('Circulating Supply')}</ValueLabel>
            {totalSupply ? (
              <StyledCardValue>
                <Balance decimals={0} value={circulatingSupply} />
              </StyledCardValue>
            ) : (
              <Skeleton height={20} width={40} />
            )}
          </StyledFlex>
        </Row>
      </CardBody>
    </StyledDmdStats>
  )
}

export default DmdStats
